import { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Icon } from 'Components';
import Heading from 'LearningManager/components/Heading';
import { useStepper } from 'LearningManager/hooks/Stepper.context';
import styles from 'LearningManager/styles/CustomSetup.module.scss';
import ConfigureSettingsCard from 'LearningManager/components/Review/ConfigureSettingsCard';
import { useStores } from 'Hooks/useStore';

const ASSIGN_OPTION_LABEL: Record<number, string> = {
	214: 'By Organization',
	35: 'By Property',
	634: 'By Role',
	676: 'By Group',
	784: 'By Individual'
};

const Review: React.FC = observer(() => {
	const { learningManagerStore } = useStores();
	const {
		draft: { configure, customSetup }
	} = useStepper();

	const { learningPaths, activities } = learningManagerStore;

	// --- Courses section ---
	const selectedLearningPaths = learningPaths.filter((lp) =>
		customSetup.selectedLearningPathCourseIds.includes(lp.id)
	);
	const selectedActivities = activities.filter((a) =>
		customSetup.selectedIndividualCourseIds.includes(a.id)
	);

	// --- Audience section ---
	const selectedOptionId = customSetup.selectedAssignUsersOptionId;
	const audienceLabel = selectedOptionId
		? ASSIGN_OPTION_LABEL[selectedOptionId] ?? 'Not selected'
		: 'Not selected';

	const audienceNames = useMemo(() => {
		switch (selectedOptionId) {
			case 214:
				return customSetup.selectedOrganizations.map((o) => o.name);
			case 35:
				return customSetup.selectedProperties.map((p) => p.name);
			case 634:
				return customSetup.selectedRoles.map((r) => r.name);
			case 676:
				return customSetup.selectedGroups.map((g) => g.name);
			case 784:
				return customSetup.selectedIndividualUsers.map(
					(u) => `${u.firstName} ${u.lastName}`
				);
			default:
				return [];
		}
	}, [selectedOptionId, customSetup]);

	const courseCount =
		selectedLearningPaths.length + selectedActivities.length;

	return (
		<div id="lms-ca-custom-review" className={styles.review}>
			<Heading
				title="Review & Confirm"
				subHeading="Review the configuration below before creating the assignment"
			/>

			<div
				id="lms-ca-custom-review-courses"
				className={styles.reviewSection}
			>
				<div className={styles.reviewSectionTitle}>
					Courses selected ({courseCount})
				</div>
				<div className={styles.courseList}>
					{selectedLearningPaths.map((lp, index) => (
						<div
							key={`lp-${lp.id}`}
							id={`lms-ca-custom-review-lp-${index}`}
							className={styles.courseItem}
						>
							<div className={styles.courseItemLeft}>
								<div className={styles.serial}>{index + 1}</div>
								<Icon type="learningPathOutline" role="img" />
								<div className={styles.courseLabel}>
									{lp.name}
								</div>
								<div className={styles.requiredTag}>
									{lp.is_required ? 'Required' : 'Optional'}
								</div>
							</div>
						</div>
					))}
					{selectedActivities.map((activity, index) => (
						<div
							key={`act-${activity.id}`}
							id={`lms-ca-custom-review-act-${index}`}
							className={styles.courseItem}
						>
							<div className={styles.courseItemLeft}>
								<div className={styles.serial}>
									{selectedLearningPaths.length + index + 1}
								</div>
								<Icon type="indiCoursesOutline" role="img" />
								<div className={styles.courseLabel}>
									{activity.name}
								</div>
								<div className={styles.requiredTag}>
									{activity.is_required
										? 'Required'
										: 'Optional'}
								</div>
							</div>
						</div>
					))}
					{courseCount === 0 && (
						<div className={styles.empty}>
							No courses selected yet.
						</div>
					)}
				</div>
			</div>

			<div
				id="lms-ca-custom-review-audience"
				className={styles.reviewSection}
			>
				<div className={styles.reviewSectionTitle}>
					Full Campaign Audience
				</div>
				<div className={styles.summaryCard}>
					<div>
						<div className={styles.mutedLabel}>
							Assigned Learners
						</div>
						<div className={styles.summaryValue}>
							{audienceLabel}
						</div>
					</div>
					{audienceNames.length > 0 && (
						<div>
							<div className={styles.mutedLabel}>Selected</div>
							<div className={styles.summaryValue}>
								{audienceNames.join(', ')}
							</div>
						</div>
					)}
				</div>
			</div>

			<div
				id="lms-ca-custom-review-configure"
				className={styles.reviewSection}
			>
				<div className={styles.reviewSectionTitle}>
					Configure Settings
				</div>
				<ConfigureSettingsCard configure={configure} />
			</div>
		</div>
	);
});

export default Review;
